// @ts-check
// Formulários das divisões por regra (spec 2.0.0, fluxos.md seção 20.7; contrato 09 de fluxos, seção 17): "Dividir por
// filtro do lead" usa o construtor de público da onda 1 (uso CONDITION, sem canais) e mostra quantos cards atendem hoje;
// "Dividir por e-mail do fluxo" escolhe um "Enviar e-mail" anterior no caminho, o que conta como SIM (abriu, clicou ou
// os dois) e, para clique, qualquer link ou um link do modelo.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { getEmailTemplate } from '../../../data/email-templates.js';
import { AudienceBuilder } from '../../audiences/audience-builder.js';
import { Checkbox } from '../../../ui/feedback.js';
import { emailNodesBefore } from '../../../lib/flow-editor-state.js';
import { FLOW_ENGAGEMENT_EVENTS } from '../../../../../shared/flows/flow-definition.js';
import { FlowField, FlowNotice, fieldProblemText, flowProblemText } from '../flow-ui.js';
import { FlowRadioOption } from '../flow-lifecycle-dialogs.js';
import { t } from '../../../i18n/index.js';

/** @typedef {import('../flow-node-panel.js').NodeFormProps} NodeFormProps */

/** @param {NodeFormProps} props */
export function ConditionForm({ node, problems, onConfig }) {
  const error = fieldProblemText(problems, 'audience') ?? (problems.find((problem) => problem.code === 'AUDIENCE_INVALID') ? flowProblemText(/** @type {any} */ (problems.find((problem) => problem.code === 'AUDIENCE_INVALID'))) : null);
  return html`<div class="flow-node-form flow-condition-form" data-testid="flow-condition-form">
    ${error ? html`<p class="flow-field-error" role="alert">${error}</p>` : null}
    <${AudienceBuilder} key=${node.id} value=${node.config.audience} usage="CONDITION" channels=${[]} showRecipients=${false}
      title=${t('flows.forms.condition.title')} onChange=${(/** @type {any} */ audience) => onConfig({ audience })} />
    <${FlowNotice} icon="IconHierarchy2">${t('flows.forms.condition.branches')}<//>
  </div>`;
}

/** @param {NodeFormProps} props */
export function EngagementForm({ node, definition, problems, lookups, onConfig }) {
  const config = node.config;
  const emails = emailNodesBefore(definition, node.id);
  const emailNode = emails.find((candidate) => candidate.id === config.emailNodeId) ?? null;
  const [links, setLinks] = useState(/** @type {Array<{ index: number, label: string, url: string }> | null} */ (null));
  const templateId = emailNode?.config?.templateId ?? null;
  useEffect(() => {
    setLinks(null);
    if (!templateId || config.event === 'OPENED') return undefined;
    let isCancelled = false;
    getEmailTemplate(templateId)
      .then((template) => !isCancelled && setLinks((template.compiled?.links ?? []).filter((link) => link.isTracked)))
      .catch(() => !isCancelled && setLinks([]));
    return () => {
      isCancelled = true;
    };
  }, [templateId, config.event]);
  const waitProblem = problems.find((problem) => problem.code === 'ENGAGEMENT_WAIT_TOO_SHORT');
  const name = `flow-engagement-event-${node.id}`;

  if (emails.length === 0) {
    return html`<div class="flow-node-form" data-testid="flow-engagement-form">
      <${FlowNotice} icon="IconAlertTriangle" tone="warning">${t('flows.forms.engagement.noEmail')}<//>
    </div>`;
  }
  return html`<div class="flow-node-form" data-testid="flow-engagement-form">
    <${FlowField} label=${t('flows.forms.engagement.email')} htmlFor=${`flow-engagement-email-${node.id}`} error=${fieldProblemText(problems, 'emailNodeId')
      ?? (problems.find((problem) => problem.code === 'ENGAGEMENT_EMAIL_NOT_BEFORE') ? t('flows.problems.ENGAGEMENT_EMAIL_NOT_BEFORE') : null)}>
      <select id=${`flow-engagement-email-${node.id}`} class="text-input" value=${config.emailNodeId ?? ''}
        onChange=${(/** @type {Event} */ event) => onConfig({ emailNodeId: /** @type {HTMLSelectElement} */ (event.currentTarget).value || null, linkIndex: null })}>
        <option value="">${t('flows.forms.engagement.chooseEmail')}</option>
        ${emails.map((email) => html`<option key=${email.id} value=${email.id}>${lookups.emailNodeLabel?.(email.id) ?? t('flows.nodes.SEND_EMAIL.name')}</option>`)}
      </select>
    <//>
    <fieldset class="flow-fieldset">
      <legend class="flow-field-label">${t('flows.forms.engagement.event')}</legend>
      <div class="flow-radio-group" role="radiogroup">
        ${FLOW_ENGAGEMENT_EVENTS.map((event) => html`<${FlowRadioOption} key=${event} name=${name} value=${event} checked=${config.event === event}
          title=${t(`flows.forms.engagement.events.${event}`)} onSelect=${() => onConfig({ event, linkIndex: event === 'OPENED' ? null : config.linkIndex })} />`)}
      </div>
    </fieldset>
    ${config.event !== 'CLICKED'
      ? html`<label class="flow-checkbox-row">
          <${Checkbox} checked=${config.ignoreAutomaticOpens !== false} onChange=${(/** @type {boolean} */ value) => onConfig({ ignoreAutomaticOpens: value })}
            ariaLabel=${t('flows.forms.engagement.ignoreAutomatic')} />
          <span>${t('flows.forms.engagement.ignoreAutomatic')}</span>
        </label>`
      : null}
    ${config.event !== 'OPENED'
      ? html`<${FlowField} label=${t('flows.forms.engagement.link')} htmlFor=${`flow-engagement-link-${node.id}`}
          error=${problems.find((problem) => problem.code === 'ENGAGEMENT_LINK_MISSING') ? t('flows.problems.ENGAGEMENT_LINK_MISSING') : null}>
          <select id=${`flow-engagement-link-${node.id}`} class="text-input" value=${config.linkIndex === null || config.linkIndex === undefined ? '' : String(config.linkIndex)}
            disabled=${!templateId}
            onChange=${(/** @type {Event} */ event) => {
              const value = /** @type {HTMLSelectElement} */ (event.currentTarget).value;
              onConfig({ linkIndex: value === '' ? null : Number(value) });
            }}>
            <option value="">${t('flows.forms.engagement.anyLink')}</option>
            ${(links ?? []).map((link) => html`<option key=${link.index} value=${String(link.index)}>${link.label || link.url}</option>`)}
          </select>
        <//>`
      : null}
    ${waitProblem ? html`<${FlowNotice} icon="IconClock" tone="warning">${flowProblemText(waitProblem)}<//>` : null}
    <${FlowNotice} icon="IconInfoCircle">${t('flows.forms.engagement.estimateNotice')}<//>
  </div>`;
}
